/**
 * Vintage Button — wraps Mantine's `<Button>` with the design system's semantic
 * variant API layered on Mantine's visual variants. Design-system names map to a
 * base Mantine variant + a `data-variant` attribute the CSS Module styles.
 *
 * Variants (pick per the emphasis a screen needs):
 *   primary      — ink-teal fill, white text. The main CTA.
 *   secondary    — outline + ink text. The companion action.
 *   soft         — brand-tint fill + ink text. A quieter primary for busy screens.
 *   accent       — brass (gold-leaf) fill + dark ink text. Premium / featured;
 *                  use sparingly, never more than one per view.
 *   success      — credit-green fill, white text. Approve / confirm.
 *   destructive  — debit-red fill, white text. Delete / decline.
 *   ghost        — transparent, subtle hover. Toolbar / tertiary.
 *   link         — text-only, underline on hover. Inline actions.
 *
 * All Mantine props still pass through (leftSection / rightSection / loading /
 * fullWidth / size / radius / color / component, etc.) and Mantine's raw variant
 * names ('filled' | 'outline' | 'subtle' | 'light' | …) remain available.
 */
import { forwardRef } from 'react';
import {
  Button as MantineButton,
  type ButtonProps as MantineButtonProps,
  type PolymorphicComponentProps,
} from '@mantine/core';
import styles from './Button.module.css';

type DesignVariant =
  | 'primary'
  | 'secondary'
  | 'soft'
  | 'accent'
  | 'success'
  | 'destructive'
  | 'ghost'
  | 'link';
type MantineVariant = NonNullable<MantineButtonProps['variant']>;

/** Underlying Mantine variant for each design-system variant. */
const MANTINE_VARIANT: Record<DesignVariant, MantineVariant> = {
  primary: 'filled',
  secondary: 'outline',
  soft: 'filled',
  accent: 'filled',
  success: 'filled',
  destructive: 'filled',
  ghost: 'subtle',
  link: 'subtle',
};

/** Design variants that pin a Mantine `color` so the loader/spinner matches. */
const FORCED_COLOR: Partial<Record<DesignVariant, string>> = {
  success: 'green',
  destructive: 'red',
};

const DESIGN_VARIANTS = new Set<string>(Object.keys(MANTINE_VARIANT));

export type ButtonVariant = DesignVariant | MantineVariant;

export interface ButtonProps extends Omit<MantineButtonProps, 'variant'> {
  /**
   * Design-system semantic variant (`primary` is the default), or Mantine's raw
   * visual variant for one-off needs.
   */
  readonly variant?: ButtonVariant;
}

export const Button = forwardRef<
  HTMLButtonElement,
  PolymorphicComponentProps<'button', ButtonProps>
>(function Button({ variant = 'primary', color, className, ...rest }, ref) {
  const isDesignVariant = DESIGN_VARIANTS.has(variant);
  const mantineVariant = isDesignVariant
    ? MANTINE_VARIANT[variant as DesignVariant]
    : variant;

  // A forced color wins; otherwise honour what the consumer passed (only forward
  // when defined, to satisfy exactOptionalPropertyTypes).
  const forced = isDesignVariant ? FORCED_COLOR[variant as DesignVariant] : undefined;
  const colorProp =
    forced !== undefined
      ? { color: forced }
      : color !== undefined
        ? { color }
        : {};
  const dataVariantProp = isDesignVariant ? { 'data-variant': variant } : {};

  return (
    <MantineButton
      ref={ref}
      variant={mantineVariant}
      {...colorProp}
      {...dataVariantProp}
      classNames={{
        root: [styles.root, className].filter(Boolean).join(' '),
      }}
      {...rest}
    />
  );
});
